
import React, { useEffect, useState, useRef } from 'react';
import { initAppStabilizer, shutdownAppStabilizer } from '@/utils/system/appStabilizer';
import GlobalErrorHandler from '@/components/system/GlobalErrorHandler';
import ErrorBoundary from '@/components/ErrorBoundary';
import { logError, ErrorSeverity } from '@/utils/error/errorLogger';
import { isInRecoveryMode } from '@/utils/deployment/recoveryUtils';
import { LazyLoadWrapper, lazyLoad } from '@/utils/performance/lazyLoad';

// Development-only components with lazy loading
const RecoveryModeNotification = lazyLoad(() => import('@/components/system/RecoveryModeNotification'));
const PerformanceMonitor = lazyLoad(() => import('@/components/system/PerformanceMonitor'));
const DebugOverlay = lazyLoad(() => import('@/components/system/DebugOverlay'));

// Use a module-scope variable to track initialization across component instances
let stabilityInitialized = false;

interface EnhancedStabilityProviderProps {
  children: React.ReactNode;
  enableDebugTools?: boolean;
}

/**
 * Enhanced Stability Provider component
 * Coordinates with StabilityProvider for optimal error handling
 */
const EnhancedStabilityProvider: React.FC<EnhancedStabilityProviderProps> = ({
  children,
  enableDebugTools = false // Changed default to false to disable debug tools by default
}) => {
  const [inRecoveryMode, setInRecoveryMode] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const initRef = useRef(false);
  
  useEffect(() => {
    // Skip initialization completely in production
    if (process.env.NODE_ENV === 'production') {
      // Just check recovery mode to render the notification if needed
      setInRecoveryMode(isInRecoveryMode());
      return;
    }
    
    // Skip initialization if already done or if base StabilityProvider is active
    if (
      stabilityInitialized || 
      initialized || 
      initRef.current || 
      (typeof window !== 'undefined' && window.__STABILITY_INITIALIZED__)
    ) {
      console.log('EnhancedStabilityProvider: Another provider already initialized, using minimal setup');
      
      // Just check recovery mode to render the notification if needed
      setInRecoveryMode(isInRecoveryMode());
      
      return;
    }
    
    // Mark as initialized locally
    initRef.current = true;
    
    // Initialize app stabilizer with optimized settings
    console.log('Initializing Enhanced StabilityProvider');
    
    try {
      // Conditional configuration based on environment
      const isProduction = process.env.NODE_ENV === 'production';
      
      // Skip most monitoring in production
      if (isProduction) {
        // Set recovery mode and return
        setInRecoveryMode(isInRecoveryMode());
        setInitialized(true);
        stabilityInitialized = true;
        
        // Set global initialization marker
        if (typeof window !== 'undefined') {
          window.__STABILITY_INITIALIZED__ = true;
          window.__STABILITY_TIMESTAMP__ = Date.now();
        }
        
        return;
      }
      
      const cleanup = initAppStabilizer({
        // Core features for all environments
        interceptErrors: true,
        
        // Only use essential features in production
        cleanupStorage: !isProduction,
        manageRecoveryMode: !isProduction,
        
        // Disable heavyweight monitoring in production
        monitorMemory: false, // Disable completely
        detectFreezes: false, // Disable completely
        
        // Reduced frequency in production
        storageCleanupInterval: 24 * 60 * 60 * 1000, // Run storage cleanup every 24 hours
      });
      
      // Check recovery mode only once
      setInRecoveryMode(isInRecoveryMode());
      
      // Mark as initialized both locally and globally
      setInitialized(true);
      stabilityInitialized = true;
      
      // Set global initialization marker
      if (typeof window !== 'undefined') {
        window.__STABILITY_INITIALIZED__ = true;
        window.__STABILITY_TIMESTAMP__ = Date.now();
      }
      
      // Cleanup on unmount
      return () => {
        console.log('Shutting down Enhanced StabilityProvider');
        
        // Clean up app stabilizer
        if (typeof cleanup === 'function') {
          cleanup();
        } else {
          shutdownAppStabilizer();
        }
        
        // Reset initialization flag on unmount - only in development for hot reloading
        if (process.env.NODE_ENV !== 'production') {
          stabilityInitialized = false;
          initRef.current = false;
          
          if (typeof window !== 'undefined') {
            window.__STABILITY_INITIALIZED__ = false;
          }
        }
      };
    } catch (error) {
      logError(
        error instanceof Error ? error : 'Failed to initialize stability provider',
        ErrorSeverity.CRITICAL,
        'EnhancedStabilityProvider'
      );
      
      // Set recovery mode flag if an error occurs
      setInRecoveryMode(true);
      
      return () => {}; // Empty cleanup
    }
  }, [initialized]);
  
  // Only render debug tools in development, when explicitly enabled, tab is active, and not in recovery mode
  const shouldShowDebugTools = 
    process.env.NODE_ENV === 'development' &&
    enableDebugTools && 
    !inRecoveryMode;
  
  // Simplified structure with fewer nested components
  return (
    <ErrorBoundary 
      componentName="AppRoot" 
      fallback={
        <div className="p-6 bg-red-50 border border-red-200 rounded-md">
          <h2 className="text-xl font-medium text-red-800 mb-2">Critical Application Error</h2>
          <p className="text-red-600 mb-4">We encountered a problem with the application.</p>
          <button 
            onClick={() => window.location.reload()} 
            className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
          >
            Reload Application
          </button>
        </div>
      }
    >
      <GlobalErrorHandler>
        {inRecoveryMode && (
          <LazyLoadWrapper>
            <RecoveryModeNotification />
          </LazyLoadWrapper>
        )}
        
        {children}
        
        {/* Only render debug tools when explicitly enabled */}
        {shouldShowDebugTools && (
          <>
            <LazyLoadWrapper>
              <PerformanceMonitor />
            </LazyLoadWrapper>
            
            <LazyLoadWrapper>
              <DebugOverlay />
            </LazyLoadWrapper>
          </>
        )}
      </GlobalErrorHandler>
    </ErrorBoundary>
  );
};

export default EnhancedStabilityProvider;
